import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Input,
  Breadcrumb,
  Tag,
  DatePicker,
  Row,
  Col,
  Grid,
  Select,
} from 'antd';
import { Link } from 'react-router-dom';
import { FileExcelOutlined, FilePdfOutlined, DownloadOutlined, RightOutlined } from '@ant-design/icons';
import { LoadableContent } from '../../components';
import moment from 'moment';
import 'moment/locale/fr';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dashboardsService } from '../../common/services';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TYPE_LABEL = {
  sos_credit: 'SOS Crédit',
  transfert: 'Transfert',
  changement_numero: 'Chgt de numéro',
  promo_dimanche: 'Promo dimanche',
  vehicule_modifie: 'Véhicule modifié',
};

const TYPE_COLOR = {
  sos_credit: 'geekblue',
  transfert: 'purple',
  changement_numero: 'orange',
  promo_dimanche: 'red',
  vehicule_modifie: 'green',
};

const Operations = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(false);

  // filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [types, setTypes] = useState([]); // ['sos_credit','transfert',...]
  const [dateRange, setDateRange] = useState(null); // [moment, moment]

  // pagination serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);

  // breakpoints pour responsive
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm; // < 576px
  const controlSize = isMobile ? 'small' : 'middle';

  // fetch
  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        q: searchTerm || undefined,
        types: types.join(',') || undefined,
        start: dateRange?.[0] ? dateRange[0].startOf('day').toISOString() : undefined,
        end: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
        sort: '-date',
      };
      const res = await dashboardsService.listAllOperations(params);
      setRows(res.items || []);
      setTotal(res.total || 0);
      setPage(res.page || 1);
      setLimit(res.limit || 20);
    } catch (err) {
      console.error('Erreur API operations/list:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]); // fetch initial & quand pagination change

  // re-fetch quand filtres changent (et reset page)
  useEffect(() => {
    setPage(1);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, types, dateRange]);

  // colonnes du tableau
  const columns = useMemo(
    () => [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        render: (t) => <Tag color={TYPE_COLOR[t] || 'default'}>{TYPE_LABEL[t] || t}</Tag>,
        filters: Object.keys(TYPE_LABEL).map((k) => ({ text: TYPE_LABEL[k], value: k })),
        filteredValue: types.length ? types : null,
        onFilter: () => true, // visuel uniquement (filtrage serveur)
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        render: (d) => moment(d).format('DD/MM/YYYY HH:mm:ss'),
        sorter: (a, b) => new Date(a.date) - new Date(b.date),
        defaultSortOrder: 'descend',
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
      {
        title: 'Montant',
        dataIndex: 'amount',
        key: 'amount',
        render: (v, r) =>
          r.type === 'sos_credit' || r.type === 'transfert'
            ? `${(v ?? 0).toLocaleString('fr-FR')}`
            : '—',
        align: 'right',
        responsive: ['sm', 'md', 'lg', 'xl'], // on peut masquer sur très petit écran si besoin
      },
      {
        title: 'Contractor / Parties',
        key: 'who',
        render: (_, r) => {
          if (r.type === 'transfert') {
            return (
              <div>
                <div>
                  <strong>De:</strong> {r.senderPhone || r.senderId || '—'}
                </div>
                <div>
                  <strong>À:</strong> {r.receiverPhone || r.receiverId || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'sos_credit') {
            return (
              <div>
                <div>
                  <strong>Contractor:</strong> {r.contractorId || '—'}
                </div>
                <div>
                  <strong>Téléphone:</strong> {r.phone || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'changement_numero') {
            return (
              <div>
                <div>
                  <strong>Ancien:</strong> {r.meta?.ancienNumero || '—'}
                </div>
                <div>
                  <strong>Nouveau:</strong> {r.meta?.nouveauNumero || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'vehicule_modifie') {
            return (
              <div>
                <div>
                  <strong>Immatriculation:</strong> {r.meta?.ancienneImmatriculation} → {r.meta?.nouvelleImmatriculation}
                </div>
                <div>
                  <strong>Tél.:</strong> {r.phone || '—'}
                </div>
              </div>
            );
          }
          if (r.type === 'promo_dimanche') {
            return (
              <div>
                <strong>Contractor:</strong> {r.contractorId || '—'}
              </div>
            );
          }
          return '—';
        },
        responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
      },
    ],
    [types]
  );

  // pagination Antd -> serveur
  const onChangeTable = (pagination /*, filters, sorter*/) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
  };

  // exports
  const filteredForExport = rows.map((r) => ({
    Type: TYPE_LABEL[r.type] || r.type,
    Date: moment(r.date).format('DD/MM/YYYY HH:mm:ss'),
    Montant: (r.amount ?? '').toString(),
    ContractorId: r.contractorId ?? '',
    Téléphone: r.phone ?? '',
    SenderId: r.senderId ?? '',
    ReceiverId: r.receiverId ?? '',
    SenderPhone: r.senderPhone ?? '',
    ReceiverPhone: r.receiverPhone ?? '',
    AncienneImmatriculation: r.meta?.ancienneImmatriculation ?? '',
    NouvelleImmatriculation: r.meta?.nouvelleImmatriculation ?? '',
    AncienNumero: r.meta?.ancienNumero ?? '',
    NouveauNumero: r.meta?.nouveauNumero ?? '',
  }));

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Opérations');
    XLSX.writeFile(wb, 'operations.xlsx');
  };
  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(filteredForExport);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'operations.csv');
  };
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Liste des opérations', 14, 10);
    autoTable(doc, {
      startY: 16,
      head: [
        [
          'Type',
          'Date',
          'Montant',
          'ContractorId',
          'Téléphone',
          'SenderPhone',
          'ReceiverPhone',
          'Ancienne Immat.',
          'Nouvelle Immat.',
          'Ancien Num.',
          'Nouveau Num.',
        ],
      ],
      body: rows.map((r) => [
        TYPE_LABEL[r.type] || r.type,
        moment(r.date).format('DD/MM/YYYY HH:mm:ss'),
        (r.amount ?? '').toString(),
        r.contractorId ?? '',
        r.phone ?? '',
        r.senderPhone ?? '',
        r.receiverPhone ?? '',
        r.meta?.ancienneImmatriculation ?? '',
        r.meta?.nouvelleImmatriculation ?? '',
        r.meta?.ancienNumero ?? '',
        r.meta?.nouveauNumero ?? '',
      ]),
    });
    doc.save('operations.pdf');
  };

  return (
    <LoadableContent isLoading={isLoading}>
      <div className="content-wrapper">
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="row">
            <div className="col-md-12">
              <Breadcrumb
                separator={<RightOutlined style={{ fontSize: 16, color: '#999', fontWeight: 'bolder' }} />}
                style={{ marginBottom: 16 }}
              >
                <Breadcrumb.Item>
                  <Link to="/dashboard" style={{ fontSize: 16, fontWeight: 'bolder' }}>
                    🏠 ACCUEIL
                  </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                  <span style={{ fontSize: 16, fontWeight: 'bolder' }}>OPÉRATIONS</span>
                </Breadcrumb.Item>
              </Breadcrumb>

              <div className="card mb-4">
                <div className="card-body">
                  {/* 🔎 Filtres — 100% width sur mobile, grille sur desktop */}
                  <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
                    <Col xs={24} md={12} lg={10}>
                      <Input
                        size={controlSize}
                        placeholder="Rechercher (téléphone, contractorId...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        allowClear
                      />
                    </Col>
                    <Col xs={24} md={12} lg={10}>
                      <Select
                        mode="multiple"
                        size={controlSize}
                        placeholder="Filtrer par type"
                        value={types}
                        onChange={setTypes}
                        style={{ width: '100%' }}
                        allowClear
                      >
                        {Object.entries(TYPE_LABEL).map(([k, v]) => (
                          <Option key={k} value={k}>
                            {v}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={24} md={12} lg={4}>
                      <RangePicker
                        size={controlSize}
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD/MM/YYYY"
                        allowClear
                        style={{ width: '100%' }}
                      />
                    </Col>
                  </Row>

                  {/* ⬇️ Actions d’export — wrap + plein largeur en mobile */}
                  <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<FileExcelOutlined />}
                        onClick={exportToExcel}
                        type="primary"
                      >
                        Exporter Excel (.xlsx)
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<DownloadOutlined />}
                        onClick={exportToCSV}
                      >
                        Exporter CSV
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Button
                        block={isMobile}
                        size={controlSize}
                        icon={<FilePdfOutlined />}
                        onClick={exportToPDF}
                        danger
                      >
                        Exporter PDF
                      </Button>
                    </Col>
                  </Row>

                  {/* Tableau */}
                  <Table
                    rowKey="_id"
                    loading={isLoading}
                    columns={columns}
                    dataSource={rows}
                    size={isMobile ? 'small' : 'middle'}
                    pagination={{
                      current: page,
                      pageSize: limit,
                      total,
                      showSizeChanger: !isMobile,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (t) => `${t} opérations`,
                      responsive: true,
                    }}
                    onChange={onChangeTable}
                    scroll={{ x: true }} // ✅ évite les débordements sur petit écran
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LoadableContent>
  );
};

export default Operations;
